import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/client';
import AddOrderPopup_Addons from './AddOrderPopup_Addons';
import DemandDetailsPopup from './DemandDetailsPopup';

const AddonsTab = ({ selectedPlanning, onRefresh, filters, currentView}) => {
  const [tableData, setTableData] = useState([]);
  const [minitoolsStock, setMinitoolsStock] = useState({ OR: 0, SP: 0 });
  const [silicagelStock, setSilicagelStock] = useState(0);
  const [months, setMonths] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [ordersData, setOrdersData] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [isAddOrderOpen, setIsAddOrderOpen] = useState(false);
  const [currentOrderData, setCurrentOrderData] = useState({ id: '', year: 0, month_num: 0, name: '' });
  const [editingCell, setEditingCell] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [detailsData, setDetailsData] = useState({});

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Fetch statuses and suppliers
  useEffect(() => {
    const fetchStatusesAndSuppliers = async () => {
      try {
        const [statusesRes, suppliersSilica, suppliersMini] = await Promise.all([
          api.get('/api/orders_statuses'),
          api.get('/api/materialsuppliers_for_type/3'),
          api.get('/api/materialsuppliers_for_type/6')
        ]);
        setStatuses(statusesRes.data);
        setSuppliers([...suppliersSilica.data, ...suppliersMini.data]);
      } catch (error) {
        console.error('Error fetching statuses and suppliers:', error);
      }
    };
    fetchStatusesAndSuppliers();
  }, []);

  // Fetch orders data
  const fetchOrdersData = async () => {
    const tuples = tableData.flatMap(row =>
      months.map(monthKey => {
        const [year, month] = monthKey.split('-');
        return [row.id, parseInt(year), parseInt(month)];
      })
    );
    if (tuples.length === 0) return;
    try {
      const response = await api.post('/api/get_orders_for_tuples', { tuples });
      const ordersMap = response.data.reduce((acc, order) => {
        acc[`${order.sku}-${order.year}-${order.month_num}`] = order;
        return acc;
      }, {});
      setOrdersData(ordersMap);
    } catch (error) {
      console.error('Error fetching addons orders:', error);
    }
  };

  useEffect(() => {
    if (tableData.length > 0) {
      fetchOrdersData();
    }
  }, [tableData]);

  // Popup management
  const openAddOrderPopup = (id, year, month, name, order = null) => {
    setCurrentOrderData({ 
      id, 
      year: parseInt(year), 
      month_num: parseInt(month), 
      name,
      initialData: order
    });
    setIsAddOrderOpen(true);
  };

  const closeAddOrderPopup = () => {
    setIsAddOrderOpen(false);
  };

  const handleOrderSave = async () => {
    closeAddOrderPopup();
    await fetchData();
    await fetchOrdersData();
  };

  // Открытие попапа с деталями
  const openDetailsPopup = (addonType, year, month) => {
    console.log('Opening popup with:', { addonType, year, month, forecastId: selectedPlanning });
    setDetailsData({ addonType, year, month });
    setIsDetailsOpen(true);
  };

  const closeDetailsPopup = () => {
    setIsDetailsOpen(false);
  };

  // Fetch data
  const fetchData = async () => {
    if (!selectedPlanning) return;
    setIsLoading(true);
    try {
      const [demandResponse, miniResponse, silicaResponse] = await Promise.all([
        api.get('/api/addons_demand', { params: { forecast_id: selectedPlanning } }),
        api.get('/api/minitoolsinventory-planning'),
        api.get('/api/silicagelinventory-planning')
      ]);

      const miniStock = miniResponse.data.reduce((acc, item) => {
        acc[item.brandID] = Number(item.QTY) || 0;
        return acc;
      }, {});
      setMinitoolsStock(miniStock);

      const silicaStock = Number(silicaResponse.data.total) || 0;
      setSilicagelStock(silicaStock);

      const monthsSet = new Set();
      const allData = [
        { id: 'OR', name: 'OR Mini Garden Tools', months: demandResponse.data.OR || {} },
        { id: 'SP', name: 'SP Mini Garden Tools', months: demandResponse.data.SP || {} },
        { id: 'DES', name: 'Dessicant Bags', months: demandResponse.data.DES || {} }
      ];

      allData.forEach(item => {
        Object.keys(item.months).forEach(monthKey => {
          monthsSet.add(monthKey);
          item.months[monthKey] = {
            to_produce: Number(item.months[monthKey].to_produce) || 0,
            fact: Number(item.months[monthKey].fact) || 0,
            plan: Number(item.months[monthKey].plan) || 0
          };
        });
      });

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      let sortedMonths = [];
      // Текущий год
      for (let month = currentMonth; month <= 12; month++) {
        sortedMonths.push(`${currentYear}-${String(month).padStart(2, '0')}`);
      }
      // Следующий год
      for (let month = 1; month <= currentMonth; month++) {
        sortedMonths.push(`${currentYear + 1}-${String(month).padStart(2, '0')}`);
      }
      setMonths(sortedMonths);

      const brandMapping = {
        'OR': 'Organo Republic',
        'SP': 'Seedphony'
      };

      const filteredData = allData.filter(row => {
        if (row.id === 'DES') return true; // Десиканты всегда показываем
        const brandName = brandMapping[row.id];
        return !filters?.selectedBrands?.length || filters.selectedBrands.includes(brandName);
      });

      setTableData(filteredData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (onRefresh) onRefresh(fetchData);
    fetchData();
  }, [selectedPlanning, onRefresh]);

  // Editing handlers
  const handleDoubleClick = (rowIndex, monthKey, type) => {
    setEditingCell({ rowIndex, monthKey, type });
  };

  const handleSave = async (id, year, month, type, value) => {
    try {
      await api.put('/api/addons_demand', {
        forecast_id: selectedPlanning,
        id,
        year: parseInt(year),
        month: parseInt(month),
        [type]: parseInt(value) || 0
      });
      fetchData();
    } catch (error) {
      console.error('Error updating addons data:', error);
    }
    setEditingCell(null);
  };

  // Get stock
  const getStock = (rowId) => {
    if (rowId === 'DES') return silicagelStock;
    return minitoolsStock[rowId] || 0;
  };

  // Calculate rolling left
  const calculateRollingLeft = (monthsData, rowId, type) => {
    // Объект для хранения остатка ("Left") для каждого месяца
    const monthlyStock = {};
    // Начальный запас для указанного rowId (OR, SP, DES)
    const currentStock = Number(getStock(rowId)) || 0;

    // Проходим по всем месяцам из состояния months
    months.forEach((monthKey) => {
  const demand = Number(monthsData[monthKey]?.[type]) || 0;
  monthlyStock[monthKey] = Number((currentStock - demand).toFixed(2));
});

    // Возвращаем объект с остатками для всех месяцев
    return monthlyStock;
  };

  // Compute orders by effective month
  const ordersByEffectiveMonth = useMemo(() => {
    const result = {};
    Object.values(ordersData).forEach(order => {
      let effectiveMonth;
      if (order.statusID === 'S' || order.statusID === 'OP') {
        effectiveMonth = `${order.year}-${String(order.month_num).padStart(2, '0')}`;
      } else if (order.statusID === 'BO' && order.bo_follow_up_date) {
        const followUpDate = new Date(order.bo_follow_up_date);
        if (isNaN(followUpDate.getTime())) return;
        const year = followUpDate.getFullYear();
        const month = followUpDate.getMonth() + 1;
        effectiveMonth = `${year}-${String(month).padStart(2, '0')}`;
      } else {
        return;
      }
      const id = order.sku; // In ordersData, 'sku' corresponds to 'id' (OR, SP, DES)
      if (!result[id]) result[id] = {};
      if (!result[id][effectiveMonth]) result[id][effectiveMonth] = 0;
      result[id][effectiveMonth] += Number(order.qty) || 0;
    });
    return result;
  }, [ordersData]);

  // Calculate rolling left with orders
  const calculateRollingLeftWithOrders = (monthsData, rowId, type, ordersByMonth) => {
    // Объект для хранения остатка с учетом заказов ("Left + Order") для каждого месяца
    const monthlyStock = {};
    // Начальный запас для указанного rowId (OR, SP, DES)
    const currentStock = Number(getStock(rowId)) || 0;

    // Проходим по всем месяцам из состояния months
    let cumulativeOrders = 0;

months.forEach((monthKey) => {
  const ordersThisMonth = ordersByMonth[rowId]?.[monthKey] || 0;
  cumulativeOrders += ordersThisMonth;
  const demand = Number(monthsData[monthKey]?.[type]) || 0;
  monthlyStock[monthKey] = Number((currentStock + cumulativeOrders - demand).toFixed(2));
});
    // Возвращаем объект с остатками, учитывающими заказы, для всех месяцев
    return monthlyStock;
  };

  // Get left color
  const getLeftColor = (currentLeft, monthlyData, currentMonthKey, type, rowId) => {
    // Если остаток <= 0, возвращаем красный цвет
    if (currentLeft <= 0) return 'bg-red-600 text-white';

    // Инициализируем переменные для отслеживания остатка и возвращаем красный цвет
    let remainingStock = currentLeft;
    let monthsCovered = 0;

    // Находим индекс текущего месяца в списке months
    const currentMonthIndex = months.indexOf(currentMonthKey);
    // Если месяц не найден, возвращаем красный цвет (на случай ошибки)
    if (currentMonthIndex === -1) return 'bg-red-600 text-white';

    // Проверяем все будущие месяцы из списка months
    for (let i = currentMonthIndex + 1; i < months.length; i++) {
      const monthKey = months[i];
      // Получаем спрос для месяца; если данных нет, спрос = 0
      const demand = Number(monthlyData[monthKey]?.[rowId]?.[type]) || 0;

      // Если остаток покрывает спрос, увеличиваем счетчик покрытых месяцев
      if (remainingStock >= demand) {
        remainingStock -= demand;
        monthsCovered++;
      } else {
        // Если остаток недостаточен, прерываем цикл
        break;
      }
    }

    // Определяем цвет на основе количества покрытых месяцев
    if (monthsCovered >= 3) return 'bg-green-600 text-white'; // Зеленый: покрывает ≥ 3 месяцев
    if (monthsCovered >= 1) return 'bg-yellow-500 text-white'; // Желтый: покрывает 1–2 месяца
    return 'bg-orange-500 text-white'; // Оранжевый: не покрывает ни одного месяца
  };

  if (!selectedPlanning) return <div>Please select a planning first</div>;

  return (
    <div className="table-wrapper">
      {isLoading ? (
        <div>Loading...</div>
      ) : tableData.length === 0 ? (
        <div>No addons data available</div>
      ) : (
        <table className="table-planning">
          <thead>
            <tr className="bg-gray-100">
              <th rowSpan="2">Addon</th>
              <th rowSpan="2">Stock</th>
              {months.map(monthKey => {
                const [year, month] = monthKey.split('-');
                const monthName = monthNames[parseInt(month) - 1];
                // span 4 for “all,” otherwise just 1
                const span = currentView === 'all' ? 4 : 1;
                return (
                  <th key={monthKey} colSpan={span}>
                    {`${monthName} ${year}`}
                  </th>
                );
              })}

            </tr>
            <tr className="bg-gray-50">
  {months.map(monthKey => (
    <React.Fragment key={monthKey}>
      {currentView === 'all' && (
        <>
          <th>Demand</th>
          <th>Left</th>
          <th>Left + Order</th>
          <th>Order</th>
        </>
      )}
      {currentView === 'left'       && <th>Left</th>}
      {currentView === 'left-order' && <th>Left + Order</th>}
    </React.Fragment>
  ))}
</tr>

          </thead>
          <tbody>
            {tableData.map((row, rowIndex) => {
              const stock = getStock(row.id);
              const rollingToProduceLeft = calculateRollingLeft(row.months, row.id, 'to_produce');
              const rollingToProduceLeftWithOrders = calculateRollingLeftWithOrders(row.months, row.id, 'to_produce', ordersByEffectiveMonth);
              const allMonthsData = tableData.reduce((acc, r) => {
                months.forEach(monthKey => {
                  acc[monthKey] = acc[monthKey] || {};
                  acc[monthKey][r.id] = r.months[monthKey] || { to_produce: 0, fact: 0, plan: 0 };
                });
                return acc;
              }, {});
              return (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>{stock}</td>
                  {months.map(monthKey => {
                    const [year, month] = monthKey.split('-');
                    const data = row.months[monthKey] || { to_produce: 0, fact: 0, plan: 0 };
                    const orderKey = `${row.id}-${year}-${parseInt(month)}`;
                    const order = ordersData[orderKey];
                    return (
                      <React.Fragment key={monthKey}>
                        {currentView === 'all' && (
                        <td className="demand-cell">
                          {filters?.selectedCalculations?.includes('Plan to Produce') && (
                            <div 
                              onDoubleClick={() => handleDoubleClick(rowIndex, monthKey, 'to_produce')} 
                              onClick={() => openDetailsPopup(row.id, year, month)}
                              className="cursor-pointer demand-value"
                            >
                              {editingCell?.rowIndex === rowIndex &&
                              editingCell?.monthKey === monthKey &&
                              editingCell?.type === 'to_produce' ? (
                                <input
                                  type="number"
                                  defaultValue={data.to_produce}
                                  onBlur={(e) => handleSave(row.id, year, month, 'to_produce_quantity', e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && e.target.blur()}
                                />
                              ) : (
                                data.to_produce
                              )}
                            </div>
                          )}
                        </td>
                        )}
                        {(currentView === 'all' || currentView === 'left') && (
                        <td className="left-cell">
                          {filters?.selectedCalculations?.includes('Plan to Produce') && (
                            <div className={`left-value ${getLeftColor(rollingToProduceLeft[monthKey] || 0, allMonthsData, monthKey, 'to_produce', row.id)}`}>
                              {Number(rollingToProduceLeft[monthKey] || 0).toFixed(0)}
                            </div>
                          )}
                        </td>
                  )}
                   {(currentView === 'all' || currentView === 'left-order') && (
                        <td className="left-plus-order-cell">
                          {filters?.selectedCalculations?.includes('Plan to Produce') && (
                            <div className={`left-value ${getLeftColor(rollingToProduceLeftWithOrders[monthKey] || 0, allMonthsData, monthKey, 'to_produce', row.id)}`}>
                              {Number(rollingToProduceLeftWithOrders[monthKey] || 0).toFixed(0)}
                            </div>
                          )}
                        </td> )}
                        {currentView === 'all' && (
                        <td>
                          {order ? (
                            <div
                              className="text-xs cursor-pointer"
                              onClick={() => openAddOrderPopup(row.id, year, parseInt(month), row.name, order)}
                            >
                              <div>Supplier: {order.supplierName}</div>
                              <div>Qty: {order.qty}</div>
                              <div>Status: {order.statusID}</div>
                            </div>
                          ) : (
                            <button
                              className="planning-btn-add-order"
                              onClick={() => openAddOrderPopup(row.id, year, parseInt(month), row.name)}
                            >
                              +
                            </button>
                          )}
                        </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {isAddOrderOpen && (
        <AddOrderPopup_Addons
          id={currentOrderData.id}
          year={currentOrderData.year}
          month_num={currentOrderData.month_num}
          name={currentOrderData.name}
          statuses={statuses}
          suppliers={suppliers}
          onClose={closeAddOrderPopup}
          onSave={handleOrderSave}
          initialData={currentOrderData.initialData}
        />
      )}
      {isDetailsOpen && (
        <DemandDetailsPopup
          materialType="addons"
          sku={detailsData.addonType}
          year={detailsData.year}
          month={detailsData.month}
          forecastId={selectedPlanning}
          onClose={closeDetailsPopup}
        />
      )}
    </div>
  );
};

export default AddonsTab;